import React, { createContext, useCallback, useState, useContext, useEffect, useRef } from 'react';
import { InternalAxiosRequestConfig } from 'axios';

import api from '../service/api';

interface User {
  id: string;
  name: string;
  email: string;
  empresa: { id: string };
  role: string;
  tipo_usuario?: string;
  colaborador?: { id?: string };
}

interface AuthState {
  token: string;
  user: User;
}

interface SignInCredentials {
  email: string;
  password: string;
}

interface AuthContextData {
  user: User;
  signIn(credentials: SignInCredentials): Promise<User>;
  signOut(): void;
  updateUser(user: User): void;
}

// Requisições que fazem parte do próprio fluxo de sessão: um 401 aqui NÃO deve
// disparar refresh (senão vira loop: refresh -> 401 -> refresh -> ...).
const ROTAS_DE_AUTH = ['/api-essencial/v1/auth', '/api-essencial/v1/auth/refresh', '/api-essencial/v1/auth/logout'];

const ehRotaDeAuth = (url?: string) => !!url && ROTAS_DE_AUTH.some((rota) => url.includes(rota));

// Renova o token com esta antecedência do vencimento. O Mapa Cirúrgico fica
// aberto na TV da logística por horas; sem isso o usuário só descobre que a
// sessão morreu quando clica em algo e cai no login.
const ANTECEDENCIA_DO_REFRESH_MS = 5 * 60 * 1000; // 5 min

// Piso pro agendamento: se o token já está quase vencendo (ou vencido) quando
// carregamos a página, renova quase imediatamente em vez de agendar no passado.
const ATRASO_MINIMO_DO_REFRESH_MS = 5 * 1000;
const INTERVALO_RETENTATIVA_DO_REFRESH_MS = 30 * 1000;

// setTimeout não é confiável pra intervalos longos: se o notebook dorme ou a
// aba fica muito tempo em background, o disparo atrasa. Ao voltar pro primeiro
// plano conferimos se o token está perto do fim e renovamos na hora.
const MARGEM_AO_VOLTAR_PRA_ABA_MS = 2 * 60 * 1000; // 2 min

// Lê o `exp` (em segundos) do payload do JWT, sem depender de lib externa.
// Retorna null se o token não for decodificável — nesse caso caímos no
// comportamento antigo (renovar só quando tomar 401).
const lerExpiracaoDoToken = (token?: string): number | null => {
  if (!token) return null;

  try {
    const payload = token.split('.')[1];
    if (!payload) return null;

    // base64url -> base64, com o padding que o atob exige.
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const preenchido = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');

    const { exp } = JSON.parse(atob(preenchido));

    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
};


const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export const AuthProvider: React.FC = ({ children }) => {
  const [data, setData] = useState<AuthState>(() => {
    const token = localStorage.getItem('@Aplication:token');
    const user = localStorage.getItem('@Aplication:user');

    if (token && user) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      return { token, user: JSON.parse(user) };
    }

    return {} as AuthState;
  });

  const limparSessao = useCallback(() => {
    localStorage.removeItem('@Aplication:token');
    localStorage.removeItem('@Aplication:user');
    delete api.defaults.headers.common['Authorization'];

    setData({} as AuthState);
  }, []);

  // Um único refresh em voo compartilhado por TODOS os disparos: o timer
  // proativo, o retorno pra aba e os vários 401 simultâneos (a tela do Mapa
  // dispara vários GETs juntos). Fica num ref porque o interceptor e o
  // agendador vivem em effects diferentes.
  const refreshEmAndamento = useRef<Promise<string> | null>(null);

  // Guarda o timer da renovação proativa pra poder reagendar/cancelar.
  const timerDoRefresh = useRef<ReturnType<typeof setTimeout> | null>(null);

  const renovarToken = useCallback(() => {
    if (!refreshEmAndamento.current) {
      refreshEmAndamento.current = api
        .post('/api-essencial/v1/auth/refresh')
        .then((response) => {
          const novoToken = response.data.token;

          localStorage.setItem('@Aplication:token', novoToken);
          api.defaults.headers.common['Authorization'] = `Bearer ${novoToken}`;
          setData((anterior) => ({ ...anterior, token: novoToken }));

          return novoToken as string;
        })
        .finally(() => {
          refreshEmAndamento.current = null;
        });
    }

    return refreshEmAndamento.current;
  }, []);

  // Interceptor de 401 registrado uma única vez (evita empilhar handlers a cada
  // login). Rede de segurança: com a renovação proativa abaixo, o token
  // normalmente nem chega a expirar — mas se o timer não disparar (aba
  // suspensa, relógio do cliente errado), este caminho ainda recupera a sessão
  // antes de derrubar o usuário. Só desloga se o refresh responder 401.
  useEffect(() => {
    const id = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const original = error.config as
          | (InternalAxiosRequestConfig & { _jaTentouRefresh?: boolean })
          | undefined;

        if (error.response?.status !== 401) {
          return Promise.reject(error);
        }

        // 401 no login = credencial errada; no refresh/logout = sessão morreu.
        if (!original || ehRotaDeAuth(original.url) || original._jaTentouRefresh) {
          if (original && original.url?.includes('/auth/refresh')) {
            limparSessao();
          }
          return Promise.reject(error);
        }

        original._jaTentouRefresh = true;

        try {
          const novoToken = await renovarToken();
          original.headers.set('Authorization', `Bearer ${novoToken}`);

          return await api(original);
        } catch (erroDoRefresh) {
          // A rejeição definitiva (401 no refresh) é tratada acima. Falhas de
          // rede, do servidor ou da requisição repetida não invalidam a sessão.
          return Promise.reject(erroDoRefresh);
        }
      }
    );

    return () => api.interceptors.response.eject(id);
  }, [limparSessao, renovarToken]);

  // ---------------------------------------------------------------------
  // Renovação PROATIVA. É o que impede o usuário de ser deslogado no meio do
  // expediente: em vez de esperar uma chamada tomar 401, agendamos a troca do
  // token pra alguns minutos antes de ele vencer. Enquanto a aba estiver
  // aberta e o refresh token continuar válido no backend, a sessão se renova
  // sozinha e o Mapa Cirúrgico pode ficar horas na tela.
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!data.token) return undefined;
    let ativo = true;

    const cancelarTimer = () => {
      if (timerDoRefresh.current) {
        clearTimeout(timerDoRefresh.current);
        timerDoRefresh.current = null;
      }
    };

    const expiraEm = lerExpiracaoDoToken(data.token);

    // Token sem `exp` legível: não dá pra agendar nada. O interceptor de 401
    // continua sendo a rede de segurança, como era antes.
    if (!expiraEm) return undefined;

    const tentarRenovar = () => {
      cancelarTimer();
      renovarToken().catch(() => {
        // O effect é cancelado ao sair ou ao trocar o token. Não deixa uma
        // tentativa pendente reagendar a renovação de uma sessão antiga.
        if (ativo) {
          timerDoRefresh.current = setTimeout(
            tentarRenovar,
            INTERVALO_RETENTATIVA_DO_REFRESH_MS
          );
        }
      });
    };

    const agendar = () => {
      cancelarTimer();

      const faltando = expiraEm - Date.now() - ANTECEDENCIA_DO_REFRESH_MS;
      const atraso = Math.max(faltando, ATRASO_MINIMO_DO_REFRESH_MS);

      timerDoRefresh.current = setTimeout(tentarRenovar, atraso);
    };

    agendar();

    // setTimeout longo é traiçoeiro: o navegador estrangula timers em abas de
    // fundo e a máquina pode dormir. Ao voltar pra aba conferimos o relógio de
    // verdade e renovamos na hora se o token estiver perto do fim.
    const aoVoltarPraAba = () => {
      if (document.visibilityState !== 'visible') return;

      if (Date.now() > expiraEm - MARGEM_AO_VOLTAR_PRA_ABA_MS) {
        tentarRenovar();
      }
    };

    document.addEventListener('visibilitychange', aoVoltarPraAba);
    window.addEventListener('online', aoVoltarPraAba);

    return () => {
      ativo = false;
      cancelarTimer();
      document.removeEventListener('visibilitychange', aoVoltarPraAba);
      window.removeEventListener('online', aoVoltarPraAba);
    };
  }, [data.token, renovarToken]);

  // Sincroniza as abas. Sem isto, a aba B segue usando o token velho depois da
  // aba A renovar — toma 401 e dispara um segundo refresh desnecessário. O
  // evento `storage` só chega nas OUTRAS abas, nunca na que gravou.
  useEffect(() => {
    const aoMudarOStorage = (evento: StorageEvent) => {
      if (evento.key === '@Aplication:token') {
        if (evento.newValue) {
          api.defaults.headers.common['Authorization'] = `Bearer ${evento.newValue}`;
          setData((anterior) => ({ ...anterior, token: evento.newValue as string }));
        } else {
          // Logout em outra aba: acompanha em vez de continuar numa sessão
          // que já morreu no backend.
          limparSessao();
        }
      }
    };

    window.addEventListener('storage', aoMudarOStorage);

    return () => window.removeEventListener('storage', aoMudarOStorage);
  }, [limparSessao]);

  const signOut = useCallback(() => {
    // Invalida a sessão no backend também (só limpar o localStorage não
    // derruba o refresh token). Não dá pra esperar: a UI já redireciona pro
    // login, e se a chamada falhar a sessão local sai do mesmo jeito.
    api.post('/api-essencial/v1/auth/logout').catch(() => undefined);

    limparSessao();
  }, [limparSessao]);

  const signIn = useCallback(async ({ email, password }) => {
    const response = await api.post('/api-essencial/v1/auth', {
      email,
      password,
    });

    // A resposta também traz refreshToken, mas ele não é usado aqui de
    // propósito: no navegador a API já grava o cookie httpOnly da sessão, e é
    // esse cookie que o POST /auth/refresh consome.
    const { token, user } = response.data;

    localStorage.setItem('@Aplication:token', token);
    localStorage.setItem('@Aplication:user', JSON.stringify(user));

    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    setData({ token, user });

    return user;
  }, []);

  const updateUser = useCallback(
    (user: User) => {
      setData({
        token: data.token,
        user,
      });

      localStorage.setItem('@Aplication:user', JSON.stringify(user));
    },

    [data.token]
  );

  return (
    <AuthContext.Provider
      value={{ user: data.user, signIn, signOut, updateUser }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
